import React, { useState, useEffect } from 'react';
import styles from './RecruitProfile.module.css';

const METRIC_EMOJI = {
  '20yd_dash': '\u26A1', 'broad_jump': '\uD83E\uDD98', 'med_ball': '\uD83D\uDCA5',
  'pushups': '\uD83D\uDCAA', '40yd_dash': '\uD83D\uDD25', 'vertical': '\uD83D\uDE80',
};
const LOWER_IS_BETTER = new Set(['20yd_dash', '40yd_dash']);

function fmtDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : d.toISOString();
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric'
  });
}

function calcAge(birthday) {
  if (!birthday) return null;
  const today = new Date();
  const dob = new Date(birthday + 'T12:00:00');
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function buildPriorBests(assessments) {
  if (assessments.length < 2) return {};
  const older = assessments.slice(1);
  const bests = {};
  for (const asm of older) {
    for (const s of (asm.scores || [])) {
      if (s.value == null) continue;
      const val = parseFloat(s.value);
      const key = s.metric_key;
      const lib = LOWER_IS_BETTER.has(key);
      if (!(key in bests)) {
        bests[key] = val;
      } else if (lib) {
        bests[key] = Math.min(bests[key], val);
      } else {
        bests[key] = Math.max(bests[key], val);
      }
    }
  }
  return bests;
}

export default function RecruitProfile({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetch(`/api/recruit/${token}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : 'error');
        return r.json();
      })
      .then(d => {
        setData(d);
        if (d.assessments?.length > 0) setExpandedId(d.assessments[0].id);
      })
      .catch(e => setError(e.message === 'not_found' ? 'not_found' : 'error'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinner} />
        <div className={styles.loadingText}>Loading athlete profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrap}>
        <div className={styles.errorCard}>
          <div className={styles.errorIcon}>{error === 'not_found' ? '🔒' : '⚠'}</div>
          <div className={styles.errorTitle}>
            {error === 'not_found' ? 'Profile Not Found' : 'Something Went Wrong'}
          </div>
          <p className={styles.errorText}>
            {error === 'not_found'
              ? 'This recruiting profile is no longer available or the link may be incorrect.'
              : 'Unable to load this profile. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  const { athlete, assessments } = data;
  const latest = assessments?.[0] || null;
  const priorBests = buildPriorBests(assessments || []);
  const scores = latest?.scores || [];
  const strengths = (latest?.strengths || []).filter(s => s && s.trim());
  const weaknesses = (latest?.weaknesses || []).filter(w => w && w.trim());

  const subtitle = [
    athlete.position,
    athlete.team,
    athlete.grad_year ? `Class of ${athlete.grad_year}` : null,
  ].filter(Boolean).join('  \u00B7  ');

  const pills = [
    athlete.height ? { label: athlete.height } : null,
    athlete.weight ? { label: `${athlete.weight} lbs` } : null,
    athlete.birthday ? { label: `Age ${calcAge(athlete.birthday)}` } : null,
  ].filter(Boolean);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.headerBar}>
        <img src="/icon-192.png" alt="Next Gen Sports" className={styles.headerLogo} />
      </div>

      <div className={styles.container}>
        {/* Hero */}
        <div className={styles.hero}>
          <h1 className={styles.heroName}>{athlete.name}</h1>
          {subtitle && <div className={styles.heroSubtitle}>{subtitle}</div>}
          {pills.length > 0 && (
            <div className={styles.heroPills}>
              {pills.map((p, i) => <span key={i} className={styles.heroPill}>{p.label}</span>)}
            </div>
          )}
        </div>

        {!latest ? (
          <div className={styles.emptyMsg}>No assessment data available yet.</div>
        ) : (
          <>
            {/* Latest Scores */}
            {scores.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={`${styles.sectionBar} ${styles.barOrange}`} />
                  Latest Scores
                </div>
                <div className={styles.latestLabel}>{fmtDate(latest.assessment_date)}</div>
                <div className={styles.scoresGrid}>
                  {scores.map(s => {
                    const emoji = METRIC_EMOJI[s.metric_key] || '\uD83C\uDFC5';
                    const prior = priorBests[s.metric_key];
                    let deltaEl = null;
                    if (prior !== undefined && s.value != null) {
                      const current = parseFloat(s.value);
                      const lib = LOWER_IS_BETTER.has(s.metric_key);
                      if (prior !== 0 && !isNaN(current) && !isNaN(prior)) {
                        const rawPct = ((current - prior) / Math.abs(prior)) * 100;
                        const improved = lib ? current < prior : current > prior;
                        const displayPct = lib ? -rawPct : rawPct;
                        const sign = displayPct > 0 ? '+' : '';
                        deltaEl = (
                          <>
                            <div className={`${styles.scoreDelta} ${improved ? styles.deltaUp : styles.deltaDown}`}>
                              {improved ? '\u25B2' : '\u25BC'} {sign}{displayPct.toFixed(1)}%
                            </div>
                            <div className={styles.scorePrior}>vs best: {prior} {s.unit}</div>
                          </>
                        );
                      }
                    }
                    return (
                      <div key={s.metric_key} className={styles.scoreTile}>
                        <div className={styles.scoreEmoji}>{emoji}</div>
                        <div className={styles.scoreLabel}>{s.label}</div>
                        <div className={styles.scoreValue}>{s.value}</div>
                        <div className={styles.scoreUnit}>{s.unit}</div>
                        {deltaEl}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Strengths */}
            {strengths.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={`${styles.sectionBar} ${styles.barGreen}`} />
                  Strengths
                </div>
                <div className={styles.card}>
                  <ul className={styles.bulletList}>
                    {strengths.map((s, i) => (
                      <li key={i} className={styles.bulletItem}>
                        <span className={`${styles.bulletIcon} ${styles.bulletIconGreen}`}>+</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Areas to Improve */}
            {weaknesses.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={`${styles.sectionBar} ${styles.barOrange}`} />
                  Areas to Improve
                </div>
                <div className={styles.card}>
                  <ul className={styles.bulletList}>
                    {weaknesses.map((w, i) => (
                      <li key={i} className={styles.bulletItem}>
                        <span className={`${styles.bulletIcon} ${styles.bulletIconOrange}`}>&rarr;</span>
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Coach Summary */}
            {latest.summary && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={`${styles.sectionBar} ${styles.barGray}`} />
                  Coach's Summary
                </div>
                <div className={styles.summary}>
                  <p className={styles.summaryQuote}>"{latest.summary}"</p>
                </div>
              </div>
            )}

            {/* Assessment History */}
            {assessments.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={`${styles.sectionBar} ${styles.barOrange}`} />
                  Assessment History
                </div>
                {assessments.map(asm => {
                  const isOpen = expandedId === asm.id;
                  const asmStrengths = (asm.strengths || []).filter(s => s && s.trim());
                  const asmWeaknesses = (asm.weaknesses || []).filter(w => w && w.trim());
                  return (
                    <div key={asm.id} className={styles.historyCard}>
                      <div className={styles.historyHeader} onClick={() => setExpandedId(isOpen ? null : asm.id)}>
                        <div className={styles.historyHeaderLeft}>
                          <span className={styles.historyDate}>{fmtDate(asm.assessment_date)}</span>
                          <span className={`${styles.historyTypeBadge} ${asm.assessment_type === 'test_day' ? styles.badgeTestDay : styles.badgeEvaluation}`}>
                            {asm.assessment_type === 'test_day' ? 'Test Day' : 'Evaluation'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className={styles.historyScoreCount}>
                            {asm.scores?.length || 0} score{(asm.scores?.length || 0) !== 1 ? 's' : ''}
                          </span>
                          <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>&#9660;</span>
                        </div>
                      </div>
                      {isOpen && (
                        <div className={styles.historyBody}>
                          {asm.scores?.length > 0 && (
                            <div className={styles.historyScoresGrid}>
                              {asm.scores.map(s => (
                                <div key={s.metric_key || s.id} className={styles.historyScoreTile}>
                                  <div className={styles.historyScoreLabel}>{s.label}</div>
                                  <div className={styles.historyScoreValue}>
                                    {s.value} <span className={styles.historyScoreUnit}>{s.unit}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {asmStrengths.length > 0 && (
                            <div className={styles.historyBullets}>
                              <div className={styles.historyBulletsLabel}>Strengths</div>
                              {asmStrengths.map((s, i) => <div key={i} className={styles.historyBullet}>+ {s}</div>)}
                            </div>
                          )}
                          {asmWeaknesses.length > 0 && (
                            <div className={styles.historyBullets}>
                              <div className={styles.historyBulletsLabel}>Areas to Improve</div>
                              {asmWeaknesses.map((w, i) => <div key={i} className={styles.historyBullet}>&rarr; {w}</div>)}
                            </div>
                          )}
                          {asm.summary && <div className={styles.historySummary}>"{asm.summary}"</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <img src="/icon-192.png" alt="Next Gen Sports" className={styles.footerLogo} />
        <p className={styles.footerSub}>Parker, CO &nbsp;|&nbsp; nextgensportsco.com</p>
      </div>
    </div>
  );
}
